// src/pages/DashboardPage.jsx (or .tsx)
import React, { useEffect, useState } from 'react';
import {
  MapPin,
  CheckCircle,
  Upload,
  Users,
  Clock,
  Image,
  Video,
  Download,
  Eye,
  Filter,
  Activity,
  Target,
  Zap,
} from 'lucide-react';
import { useTheme } from '../theme/ThemeContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// ⚙️ Adjust this to match the rest of your frontend:


const DashboardPage = () => {
  const { theme } = useTheme();
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const getAuthToken = () =>
    localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

  const getAuthRole = () =>
    localStorage.getItem('authRole') || sessionStorage.getItem('authRole');
  // ✅ state from backend
  const [stats, setStats] = useState([
    {
      key: 'totalVisits',
      title: 'Total Visits Today',
      value: '0',
      change: '0%',
      trend: 'neutral',
      icon: MapPin,
    },
    {
      key: 'completedVisits',
      title: 'Completed',
      value: '0',
      change: '0%',
      trend: 'neutral',
      icon: CheckCircle,
    },
    {
      key: 'pendingUpload',
      title: 'Pending Upload',
      value: '0',
      change: '0',
      trend: 'neutral',
      icon: Upload,
    },
    {
      key: 'activeGuards',
      title: 'Active Guards',
      value: '0',
      change: '0%',
      trend: 'neutral',
      icon: Users,
    },
  ]);
    const logoutAndRedirect = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authRole');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authRole');
    navigate('/');
  };

  const [recentVisits, setRecentVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate= useNavigate();

  // 🔐 If you store token in localStorage
  function getAuthHeaders() {
     const token = getAuthToken();
      const role = getAuthRole();

      // No token or not admin → send to login
      if (!token || role !== 'admin') {
        toast.error('Session expired. Please log in again.');
        logoutAndRedirect();
        return;
      } // adjust key if different
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  async function fetchDashboard() {
    try {
      setLoading(true);
      setError('');

      const res = await fetch(
        `https://patrol-visit.patroll-management.nissiindia.com/api/dashboard?period=${selectedPeriod}`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to load dashboard');
      }

      // Map backend totals → existing card structure
      const totals = data.totals || {};

      const periodLabel =
        selectedPeriod === 'today'
          ? 'Today'
          : selectedPeriod === 'week'
          ? 'This Week'
          : 'This Month';

      setStats((prev) =>
        prev.map((card) => {
          const num = totals[card.key] || 0;

          // You can compute "change" from another API later; for now keep neutral or fake %.
          const change =
            card.key === 'totalVisits' && num > 0
              ? '+12%' // demo
              : '0%';

          const trend =
            change.startsWith('+') && change !== '0%'
              ? 'up'
              : change.startsWith('-')
              ? 'down'
              : 'neutral';

          let title = card.title;
          if (card.key === 'totalVisits') {
            title = `Total Visits ${periodLabel}`;
          }

          return {
            ...card,
            title,
            value: String(num),
            change,
            trend,
          };
        })
      );

      setRecentVisits(data.recentVisits || []);
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      delivered: {
        bg: 'bg-emerald-100',
        text: 'text-emerald-700',
        label: 'Delivered',
        icon: CheckCircle,
      },
      uploading: {
        bg: 'bg-blue-100',
        text: 'text-blue-700',
        label: 'Uploading',
        icon: Upload,
      },
      captured: {
        bg: 'bg-amber-100',
        text: 'text-amber-700',
        label: 'Captured',
        icon: Clock,
      },
    };
    const config =
      statusConfig[status] || statusConfig.captured;
    const Icon = config.icon;
    return (
      <span
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  return (
    <main className="flex-1 p-6 lg:p-8">
      {/* Welcome */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">
              Welcome back, Admin! 👋
            </h2>
            <p className="text-slate-600 mt-1">
              Here's what's happening with your patrol operations today
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-4 py-2 bg-white border border-slate-200 rounded-xl focus:outline-none transition-all"
              style={{
                borderColor: 'rgb(226 232 240)',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = theme.primary[500];
                e.target.style.boxShadow = `0 0 0 3px ${theme.primary[500]}33`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgb(226 232 240)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
            <button
              className="p-2 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
              onClick={fetchDashboard}
            >
              <Filter className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>
        {loading && (
          <p className="mt-2 text-sm text-slate-500">
            Loading dashboard...
          </p>
        )}
        {error && (
          <p className="mt-2 text-sm text-red-500">
            {error}
          </p>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.key}
              className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-slate-100 hover:scale-105 cursor-pointer group"
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className="p-3 rounded-xl group-hover:scale-110 transition-transform"
                  style={{
                    backgroundColor: `${theme.primary[50]}`,
                  }}
                >
                  <Icon
                    className="w-6 h-6"
                    style={{ color: theme.primary[600] }}
                  />
                </div>
                <span
                  className={`text-xs font-semibold px-2 py-1 rounded-full ${
                    stat.trend === 'up'
                      ? 'bg-emerald-100 text-emerald-700'
                      : stat.trend === 'down'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {stat.change}
                </span>
              </div>
              <h3 className="text-slate-600 text-sm font-medium mb-1">
                {stat.title}
              </h3>
              <p className="text-3xl font-bold text-slate-800">
                {stat.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Feature cards – unchanged */}
      {/* ... your AI Insights / Smart Routes / Live Tracking blocks stay the same ... */}

      {/* Recent visits table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-800">
              Recent Visits
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              Latest property patrol activities
            </p>
          </div>
          
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Property
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Guard
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Media
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Status
                </th>
               
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recentVisits.map((visit) => (
                <tr
                  key={visit.id}
                  className="hover:bg-slate-50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{
                          background: `linear-gradient(135deg, ${theme.primary[100]}, ${theme.secondary[100]})`,
                        }}
                      >
                        <MapPin
                          className="w-5 h-5"
                          style={{ color: theme.primary[600] }}
                        />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">
                          {visit.property}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {visit.client}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{
                          background: `linear-gradient(135deg, ${theme.primary[500]}, ${theme.secondary[500]})`,
                        }}
                      >
                        <span className="text-white text-xs font-semibold">
                          {visit.guard
                            ?.split(' ')
                            .map((n) => n[0])
                            .join('')}
                        </span>
                      </div>
                      <span className="text-sm text-slate-700">
                        {visit.guard}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <Clock className="w-4 h-4" />
                      {visit.time}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Image className="w-4 h-4" />
                        <span>{visit.photos}</span>
                      </div>
                      {visit.video && (
                        <div
                          className="flex items-center gap-1 text-sm"
                          style={{ color: theme.primary[600] }}
                        >
                          <Video className="w-4 h-4" />
                          <span>1</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(visit.status)}
                  </td>
                  
                </tr>
              ))}

              {!loading && recentVisits.length === 0 && !error && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-6 text-center text-sm text-slate-500"
                  >
                    No visits found for this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
          <p className="text-sm text-slate-600">
            Showing {recentVisits.length} visits
          </p>
         <button
  className="px-4 py-2 text-sm font-medium rounded-lg transition-all hover:shadow-md"
  style={{
    color: theme.primary[600],
    backgroundColor: `${theme.primary[50]}`,
  }}
  onClick={() => navigate('/visits')}
>
  View all visits →
</button>
        </div>
      </div>
    </main>
  );
};

export default DashboardPage;
